import moment from 'moment-timezone';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import {
  AIDENTIFIED_FIELD_COUNT,
  AIDENTIFIED_FILE_EXTENSIONS,
  AIDENTIFIED_MAX_FILE_BYTES,
  DATE_TIME_FORMAT,
} from '../../../constants';
import {
  checkAidentifiedFile,
  downloadAidentifiedTemplate,
  uploadAidentifiedFile,
} from '../../../services/aidentifiedDataApi';
import { UploadResult } from '../../AidentifiedData/UploadResult';
import {
  BasicModal,
  Button,
  CircularProgress,
  ModalHeader,
} from '../../Shared';

const NOTICE_CLASS_NAME = 'rounded-xl p-3 text-[14px]/[20px] text-base-1000';

const PHASE = {
  IDLE: 'idle',
  CHECKING: 'checking',
  UPLOADING: 'uploading',
  DONE: 'done',
};

const getLocalFileError = file => {
  if (!AIDENTIFIED_FILE_EXTENSIONS.test(file.name)) {
    return 'Use an .xlsx or .csv file.';
  }
  if (file.size > AIDENTIFIED_MAX_FILE_BYTES) {
    return 'The file is too large. The limit is 15 MB.';
  }
  return '';
};

const formatExportDate = exportDate =>
  moment(exportDate, DATE_TIME_FORMAT.YYYY_MM_DD_DASH).format(
    DATE_TIME_FORMAT.MMM_D_YYYY,
  );

export const AidentifiedUploadModal = ({
  clients,
  initialTenantId,
  onClose,
  onUploaded,
}) => {
  // A newer check replaces an older one still running, e.g. when the client is changed.
  const checkIdRef = useRef(0);

  const [tenantId, setTenantId] = useState(initialTenantId ?? '');
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [result, setResult] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const clientName =
    clients.find(client => client.TenantID === Number(tenantId))?.TenantName ??
    '';
  const isUploading = phase === PHASE.UPLOADING;

  const runCheck = async (nextTenantId, nextFile) => {
    checkIdRef.current += 1;
    const checkId = checkIdRef.current;

    setCheck(null);
    setFileError(null);

    if (!nextTenantId || !nextFile) {
      setPhase(PHASE.IDLE);
      return;
    }

    setPhase(PHASE.CHECKING);

    try {
      const nextCheck = await checkAidentifiedFile(nextTenantId, nextFile);
      if (checkId !== checkIdRef.current) return;
      setCheck(nextCheck);
    } catch (e) {
      if (checkId !== checkIdRef.current) return;
      setFileError({
        message: e.message || 'Failed to check the file. Try again.',
        missingColumns: e.missingColumns,
        missingColumnsCount: e.missingColumnsCount,
      });
    }
    setPhase(PHASE.IDLE);
  };

  const onChangeClient = e => {
    const nextTenantId = e.target.value;
    setTenantId(nextTenantId);
    runCheck(nextTenantId, file);
  };

  const onTakeFile = nextFile => {
    if (!nextFile) return;

    const localError = getLocalFileError(nextFile);
    if (localError) {
      toast.error(localError, { position: 'top-right', autoClose: 3000 });
      return;
    }

    setFile(nextFile);
    runCheck(tenantId, nextFile);
  };

  const onRemoveFile = () => {
    setFile(null);
    runCheck(tenantId, null);
  };

  const onDrop = e => {
    e.preventDefault();
    setIsDragOver(false);
    onTakeFile(e.dataTransfer.files?.[0]);
  };

  const onDownloadTemplate = async () => {
    try {
      await downloadAidentifiedTemplate();
    } catch {
      toast.error('Failed to download the template!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const onUpload = async () => {
    setPhase(PHASE.UPLOADING);

    try {
      const nextResult = await uploadAidentifiedFile(tenantId, file);
      setResult(nextResult);
      setPhase(PHASE.DONE);
      onUploaded();
    } catch (e) {
      setPhase(PHASE.IDLE);
      setCheck(null);
      setFileError({
        message: e.message || 'Failed to upload the file. Try again.',
        missingColumns: e.missingColumns,
        missingColumnsCount: e.missingColumnsCount,
      });
    }
  };

  const templateButton = (
    <button
      type="button"
      className="text-brand-900 underline text-[14px]/[20px]"
      onClick={onDownloadTemplate}
    >
      Download template
    </button>
  );

  return (
    <BasicModal open onClose={isUploading ? undefined : onClose}>
      <div className="bg-white w-[640px] max-w-full rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <ModalHeader
          title="Upload Aidentified data"
          subtitle="Rows are matched to the client's leads by Aidentified ID from earlier uploads, then phone, then email. Upload after the verified leads are imported in Access."
          disabled={isUploading}
          onClose={onClose}
        />
        {phase === PHASE.DONE ? (
          <div className="flex flex-col gap-4 px-5 pb-5">
            <div className={twMerge(NOTICE_CLASS_NAME, 'bg-brand-300/15')}>
              <b>
                {result.rowsMatched.toLocaleString('en-US')} of{' '}
                {result.rowsTotal.toLocaleString('en-US')} rows matched{' '}
                {result.tenantName} leads and were saved.
              </b>{' '}
              {result.rowsWithoutLead > 0 &&
                `The other ${result.rowsWithoutLead.toLocaleString('en-US')} were not saved. Upload the file again if more leads are imported later.`}
            </div>
            <UploadResult upload={result} />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 px-5">
              <label className="flex flex-col gap-1 text-base-1000 font-medium text-[16px]/[24px]">
                Client
                <select
                  value={tenantId}
                  disabled={isUploading}
                  onChange={onChangeClient}
                  className="h-12 rounded-xl border border-brand-300/30 px-3 font-normal outline-none focus:border-brand-300 text-align-last-left"
                >
                  <option value="" disabled>
                    Select a client
                  </option>
                  {clients.map(client => (
                    <option key={client.TenantID} value={client.TenantID}>
                      {client.TenantName}
                    </option>
                  ))}
                </select>
              </label>
              {file ? (
                <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-brand-300/50 p-4">
                  <span className="text-base-1000 font-medium text-[16px]/[24px] break-all">
                    {file.name}
                  </span>
                  {phase === PHASE.CHECKING && (
                    <span className="text-base-800 text-[14px]/[20px]">
                      Checking the file…
                    </span>
                  )}
                  {!tenantId && (
                    <span className="text-base-800 text-[14px]/[20px]">
                      Select a client to check the file.
                    </span>
                  )}
                  {check && (
                    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-base-800 text-[14px]/[20px]">
                      <span>
                        {check.rows.toLocaleString('en-US')}{' '}
                        {check.rows === 1 ? 'person' : 'people'}
                      </span>
                      <span>{check.columns} columns</span>
                      {check.exportDate && (
                        <span>
                          Export date {formatExportDate(check.exportDate)}
                        </span>
                      )}
                    </div>
                  )}
                  {check?.warnings.map(warning => (
                    <div
                      key={warning}
                      className={twMerge(
                        NOTICE_CLASS_NAME,
                        'w-full bg-rejected-500/20',
                      )}
                    >
                      {warning}. Check the client before uploading.
                    </div>
                  ))}
                  {fileError && (
                    <div
                      className={twMerge(
                        NOTICE_CLASS_NAME,
                        'w-full bg-destructive-500/10 flex flex-col gap-1',
                      )}
                    >
                      <b>{fileError.message}</b>
                      {fileError.missingColumns?.length > 0 && (
                        <span>
                          Missing columns: {fileError.missingColumns.join(', ')}
                          {fileError.missingColumnsCount >
                            fileError.missingColumns.length &&
                            ` and ${fileError.missingColumnsCount - fileError.missingColumns.length} more`}
                          . Export the full file from Aidentified, or compare it
                          with the template.
                        </span>
                      )}
                      {fileError.missingColumns?.length > 0 && (
                        <span>{templateButton}</span>
                      )}
                    </div>
                  )}
                  {isUploading ? (
                    <div className="flex items-center gap-2 text-base-800 text-[14px]/[20px]">
                      <CircularProgress size={20} />
                      Matching rows to {clientName} leads…
                    </div>
                  ) : (
                    <Button
                      type="button"
                      title="Remove file"
                      variant="blue"
                      className="h-10 py-2 px-4 text-[14px]/[20px]"
                      onClick={onRemoveFile}
                    />
                  )}
                </div>
              ) : (
                <div
                  className={twMerge(
                    'flex flex-col items-center gap-2 rounded-xl border border-dashed border-brand-300/50 p-6 text-center',
                    isDragOver && 'bg-brand-300/10',
                  )}
                  onDragOver={e => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={onDrop}
                >
                  <span className="text-base-1000 font-medium text-[16px]/[24px]">
                    Drag the Aidentified export here
                  </span>
                  <span className="text-base-800 text-[14px]/[20px]">
                    .xlsx or .csv · {AIDENTIFIED_FIELD_COUNT} Aidentified
                    columns · the first sheet is read, Export Summary is ignored
                  </span>
                  <label className="h-10 py-2 px-4 rounded-full bg-brand-100 hover:bg-brand-200 text-brand-900 font-medium text-[14px]/[24px] cursor-pointer">
                    Browse
                    <input
                      type="file"
                      accept=".xlsx,.csv"
                      aria-label="Aidentified file"
                      hidden
                      onChange={e => {
                        onTakeFile(e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {templateButton}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 py-4 px-5">
              <Button
                type="button"
                title="Cancel"
                variant="text"
                disabled={isUploading}
                onClick={onClose}
              />
              <Button
                type="button"
                title="Upload"
                disabled={!check || phase !== PHASE.IDLE}
                onClick={onUpload}
              />
            </div>
          </>
        )}
      </div>
    </BasicModal>
  );
};
